import React, { useState, useRef, useCallback, useEffect } from "react";
import ReactCrop, { Crop, PixelCrop } from "react-image-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@libs/zapi-ui/shadcn";
import { AppButton } from "@libs/zapi-ui/custom";
import { Save } from "lucide-react";
import { toast } from "sonner";
import "react-image-crop/dist/ReactCrop.css";
import { CropCoordinates } from "../types";

interface RecropModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (coordinates: CropCoordinates) => void;
  imageUrl: string;
  currentCropCoordinates: CropCoordinates;
}

export function RecropModal({ isOpen, onClose, onSuccess, imageUrl, currentCropCoordinates }: RecropModalProps) {
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [isLoading, setIsLoading] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Crop history for undo
  const [cropHistory, setCropHistory] = useState<Crop[]>([]);
  const [historyIndex, setHistoryIndex] = useState<number>(-1);

  const contentRef = useRef<HTMLDivElement>(null);

  // Convert current crop coordinates to percentage-based crop for ReactCrop
  const convertCoordinatesToCrop = useCallback((coordinates: CropCoordinates, imageElement: HTMLImageElement): Crop => {
    const scaleX = imageElement.naturalWidth / imageElement.width;
    const scaleY = imageElement.naturalHeight / imageElement.height;

    const cropX = coordinates.topLeft.x / scaleX;
    const cropY = coordinates.topLeft.y / scaleY;
    const cropWidth = (coordinates.bottomRight.x - coordinates.topLeft.x) / scaleX;
    const cropHeight = (coordinates.bottomRight.y - coordinates.topLeft.y) / scaleY;

    return {
      unit: "px" as const,
      x: cropX,
      y: cropY,
      width: cropWidth,
      height: cropHeight
    };
  }, []);

  const handleImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const imageElement = e.currentTarget;
      const initialCrop = convertCoordinatesToCrop(currentCropCoordinates, imageElement);
      setCrop(initialCrop);
      setCropHistory([initialCrop]);
      setHistoryIndex(0);
    },
    [currentCropCoordinates, convertCoordinatesToCrop]
  );

  // On crop complete (mouse up), push to history
  const handleCropComplete = useCallback(
    (c: PixelCrop, percentCrop: Crop) => {
      setCompletedCrop(c);
      setCrop((prev) => {
        if (!prev) return prev;
        setCropHistory((hist) => {
          const newHist = hist.slice(0, historyIndex + 1);
          newHist.push(percentCrop);
          setHistoryIndex(newHist.length - 1);
          return newHist;
        });
        return prev;
      });
    },
    [historyIndex]
  );

  const handleSaveCrop = useCallback(() => {
    if (!completedCrop || !imgRef.current) {
      toast.error("Please select a crop area first");
      return;
    }
    setIsLoading(true);
    try {
      // Convert completedCrop to CropCoordinates
      const imageElement = imgRef.current;
      const scaleX = imageElement.naturalWidth / imageElement.width;
      const scaleY = imageElement.naturalHeight / imageElement.height;
      const coordinates: CropCoordinates = {
        topLeft: {
          x: Math.round(completedCrop.x * scaleX),
          y: Math.round(completedCrop.y * scaleY)
        },
        bottomRight: {
          x: Math.round((completedCrop.x + completedCrop.width) * scaleX),
          y: Math.round((completedCrop.y + completedCrop.height) * scaleY)
        }
      };
      onSuccess(coordinates);
      onClose();
      toast.success("Image recropped successfully!");
    } catch (error) {
      console.error("Error processing recrop:", error);
      toast.error("Failed to process recrop");
    } finally {
      setIsLoading(false);
    }
  }, [completedCrop, onSuccess, onClose]);

  // Save on Enter and Undo (trap events in modal)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "z") {
        e.preventDefault();
        e.stopPropagation();
        setHistoryIndex((idx) => {
          if (idx > 0) {
            setCrop(cropHistory[idx - 1]);
            return idx - 1;
          }
          return idx;
        });
      } else if (e.key === "Enter" && !isLoading) {
        e.preventDefault();
        e.stopPropagation();
        handleSaveCrop();
      }
    };
    const ref = contentRef.current;
    if (ref) {
      ref.addEventListener("keydown", handleKeyDown);
      ref.focus();
    }
    return () => {
      if (ref) ref.removeEventListener("keydown", handleKeyDown);
    };
  }, [cropHistory, isLoading, handleSaveCrop]);

  const handleClose = useCallback(() => {
    if (!isLoading) {
      onClose();
    }
  }, [isLoading, onClose]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setCrop(undefined);
      setCompletedCrop(undefined);
      setIsLoading(false);
      setCropHistory([]);
      setHistoryIndex(-1);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-hidden"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          contentRef.current?.focus();
        }}
      >
        <div ref={contentRef} tabIndex={0} style={{ outline: "none" }}>
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">Recrop Image</DialogTitle>
            <DialogDescription>Adjust the selection to crop the image as desired.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 overflow-auto">
            <div className="flex justify-center">
              <ReactCrop
                crop={crop}
                onChange={setCrop}
                onComplete={handleCropComplete}
                aspect={undefined}
                className="max-w-full max-h-[60vh] object-contain"
              >
                <img
                  ref={imgRef}
                  alt="Recrop image"
                  src={imageUrl}
                  onLoad={handleImageLoad}
                  className="max-w-full max-h-[60vh] object-contain"
                />
              </ReactCrop>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <AppButton data-testid="recrop-modal-cancel-button" appearance="outlined" onClick={handleClose} disabled={isLoading}>
                Cancel
              </AppButton>
              <AppButton data-testid="recrop-modal-save-button" onClick={handleSaveCrop} disabled={!completedCrop || isLoading}>
                <Save className="w-4 h-4" />
                {isLoading ? "Saving..." : "Save Crop"}
              </AppButton>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
